import React, { useState, useEffect, useContext, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { ActionLink, ModalWindow, Input } from '../../atoms';
import { Button, Pagination, Table, } from 'rsuite';
import Dropzone from 'react-dropzone';

import axios from '../../../utils/axios';
import { getMetaCatalogPDFs } from '../../../services/dash.service';
import { UserContext } from '../../../context';
import { default as t } from '../../../utils/i18n';

import './add-catalog-data.scss';

const getWidth = (value = 10) => {
  return window.innerWidth / value;
};

const TableCell = ({ rowData, dataKey, ...props }) => {
  return (
    <Table.Cell {...props} title={rowData[dataKey]}>
      {rowData[dataKey] || "-"}
    </Table.Cell>
  );
};

const DateCell = ({ rowData, dataKey, ...props }) => {
  const date = rowData[dataKey] ? new Date(rowData[dataKey]) : null;
  const formatted = date ? date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }) : "-";
  
  return (
    <Table.Cell {...props} title={formatted}>
      {formatted}
    </Table.Cell>
  );
};

const AddCatalogData = props => {
  const { user } = useContext(UserContext);
  const userAccessToken = user?.tokens?.accessToken?.jwtToken;
  const username = user?.username;
  const [tableData, setTableData] = useState({ metaCatalogFiles: [], pagination: {} });
  const [showModal, setShowModal] = useState(false);
  const [searchItem, setSearchItem] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [fileName, setFileName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [filesUploaded, setUploadedFiles] = useState(null);
  const [fileUploadFailed, setFileUploadFailed] = useState(null);

  const fetchTableData = async (currentPage, pageLimit, search) => {
    try {
      const results = await getMetaCatalogPDFs(userAccessToken, username, pageLimit, currentPage, search);
      setTableData(results);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const debouncedSearch = useMemo(
    () => debounce((searchValue) => {
      // change page to 1 when searching
      fetchTableData(1, limit, searchValue);
    }, 500),
    [limit]
  );

  // Initial data load
  useEffect(() => {
    fetchTableData(page, limit, searchItem);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handlePaginationChange = (newPage, newLimit) => {
    fetchTableData(newPage, newLimit, searchItem);
  };

  const handleSearchChange = (value) => {
    setSearchItem(value);
    debouncedSearch(value);
  };

  const loadMore = (currentPage) => {
    setPage(currentPage);
    handlePaginationChange(currentPage, limit);
  };

  const handleChangeLimit = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    handlePaginationChange(1, newLimit);
  };

  const onDrop = async files => {
    const file = files?.[0];

    if (!file) return;

    const isPdf = 
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      setFileUploadFailed('Only PDF files are allowed');
      return;
    }

    setFileUploadFailed(null);
    setUploadedFiles(files);
    setFileName(file.name);
  };

  const handleUpload = async () => {
    if (!filesUploaded?.[0]) return;
    const file = filesUploaded[0];

    const requestHeaders = {
      'x-access-token': userAccessToken,
      'x-user': username,
    }

    try {
      // Step 1: Get presigned URL
      const presignedRes = await axios.get(
        `${process.env.REACT_APP_MALA_URL}/api/admin/meta-catalogs/getSignedUrl`, 
        {
          params: { fileName },
          headers: requestHeaders
        }
      );

      const { data: { presignedUrl } } = presignedRes.data;

      // Step 2: Upload file to S3 using PUT
      await axios.put(
        presignedUrl, 
        file, 
        {
          headers: {
            'Content-Type': 'application/pdf',
             ...requestHeaders
          }
        }
      );

      // Step 3: Send metadata
      await axios.post(
        `${process.env.REACT_APP_MALA_URL}/api/admin/meta-catalogs/add`, 
        {
          fileName,
          prefix,
        }, 
        { headers: requestHeaders }
      );

      // Refresh data after successful upload
      fetchTableData(page, limit, searchItem);
      setShowModal(false);
      // Reset form fields
      setFileName('');
      setPrefix('');
      setUploadedFiles(null);
    } catch (error) {
      console.error(error);
      setFileUploadFailed('Error adding data.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFileUploadFailed(null)
    setUploadedFiles(null)
    setFileName("")
    setPrefix("")
  }

  return (
    <div className="catalog-data-container">
      <button className="return-insights" onClick={props.handleBackClick}>
        <i className="eva  eva-arrow-ios-back-outline"></i>
        Back
      </button>
      <div className="catalog-data-header-section">
        <div className="catalog-data-header-container">
          <h2>Meta Catalog PDF Upload</h2>
          <p>
            Upload and manage your meta catalog PDF's in one place. Changes made here will
            automatically reflect in Mala - Meta Catalog Route of Add Experiments.
          </p>
        </div>
        <div className="catalog-data-header-action">
          <button className="step-back step-add" onClick={() => setShowModal(true)}>
            Upload PDF
          </button>
          <div className="search-section-container search_onboarded">
            <div className="search-field search_onboarded_icon">
              <i className="eva eva-search-outline search-icon"></i>
              <input
                type="text"
                name="search"
                placeholder="Search for the catalog"
                onChange={e => handleSearchChange(e.target.value)}
                value={searchItem}
                autoComplete="off"
              />
              <button
                type="button"
                className={`clear-btn ${searchItem ? 'show' : ''}`}
                onClick={() => handleSearchChange('')}
              >
                <i className="eva eva-close-outline clear-icon"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="attr-delete accounts-add-container">
        <ModalWindow
          className="fqa-modal--addAccounts add-bm"
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
          heading="Upload PDF"
          ariaHideApp={false}
          appElement={document.getElementById('app')}
        >
          <div className="container_excel">
            <Dropzone onDrop={onDrop} accept="application/pdf">
              {({ getRootProps, getInputProps }) => (
                <section className="container">
                  {!filesUploaded?.length && (
                    <div {...getRootProps({ className: 'dropzone' })}>
                      <input {...getInputProps()} />
                      <p className="drag_file">{t('admin.add.drag')}</p>
                      <p className="drag_file"> {t('admin.add.or')}</p>
                      <p className="drag_file text_dec">{t('admin.add.browse')}</p>
                    </div>
                  )}
                  <aside>
                    {filesUploaded?.length && (
                      <ul className="dropzone uploaded_file">
                        <i className="eva eva-file-outline"></i>&nbsp;{filesUploaded[0].name}
                      </ul>
                    )}
                  </aside>
                </section>
              )}
            </Dropzone>
          </div>
          {fileUploadFailed && <div className="error">{fileUploadFailed}</div>}
          <div>
            <div className="input-container add_bm">
              <Input
                labelValue="Prefix"
                name="name"
                fieldType="text"
                placeholder="Example : MPAW6P_A_ "
                value={prefix}
                handleChange={e => setPrefix(e.target.value)}
              />
            </div>
          </div>
          <div className="fqa-model-pa--actions">
            <ActionLink value="Close" anchor={true} target={false} handleClick={closeModal} />
            <Button
              className="fqa-model-pa--actions-confirm delete-button"
              name="submit"
              onClick={handleUpload}
              disabled={!fileName || !prefix}
            >
              Upload
            </Button>
          </div>
        </ModalWindow>
      </div>

      <div className="report-performance">
        <Table
          // isTree={true}
          rowKey="id"
          data={tableData?.metaCatalogFiles}
          headerHeight={50}
          autoHeight={true}
          virtualized
        >
          <Table.Column width={getWidth(3)}>
            <Table.HeaderCell>Uploaded PDF</Table.HeaderCell>
            <TableCell dataKey="fileName"></TableCell>
          </Table.Column>

          <Table.Column width={getWidth(4)}>
            <Table.HeaderCell>Prefix</Table.HeaderCell>
            <TableCell dataKey="prefix" />
          </Table.Column>

          <Table.Column width={getWidth(6)}>
            <Table.HeaderCell>Uploaded On</Table.HeaderCell>
            <DateCell dataKey="createdAt" />
          </Table.Column>

          <Table.Column width={getWidth(6)}>
            <Table.HeaderCell>Uploaded By</Table.HeaderCell>
            <TableCell dataKey="uploadedBy" />
          </Table.Column>
        </Table>
      </div>

      <div className="pagination-div">
        <span>Rows per page</span>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          layout={['-', 'limit', '|', 'pager']}
          total={tableData?.pagination?.totalRecords}
          limitOptions={[10, 15]}
          limit={limit}
          activePage={page}
          onChangePage={loadMore}
          onChangeLimit={handleChangeLimit}
        />
      </div>
    </div>
  );
};

export default AddCatalogData;